import React from 'react';
import { bool, number, oneOf, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { convertMoneyToNumber } from '../../util/currency';
import { PRICING_MODELS, priceUnitMessageId } from '../../util/listingPackages';

import css from './PackagePrice.css';

const { Money } = sdkTypes;

const BADGE_MESSAGE_IDS = {
  hourly: 'ListingPackages.badgeHourly',
  monthly: 'ListingPackages.badgeMonthly',
  project: 'ListingPackages.badgeProject',
};

export const PricingModelBadge = ({ pricingModel }) => (
  <span className={css.badge}>
    <FormattedMessage id={BADGE_MESSAGE_IDS[pricingModel] || BADGE_MESSAGE_IDS.project} />
  </span>
);

PricingModelBadge.propTypes = { pricingModel: oneOf(PRICING_MODELS).isRequired };

// Whole major units print without decimals ("1 500 €"); other amounts keep
// two ("1 500,50 €").
const formatPackagePrice = (intl, { amount, currency }) => {
  const value = convertMoneyToNumber(new Money(amount, currency));
  const fractionDigits = Number.isInteger(value) ? 0 : 2;
  return intl.formatNumber(value, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    useGrouping: true,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

// price is { amount, currency } in minor units (see toEnquiryPackage).
// className and amountClassName let a caller restyle the price; the caller's
// rules need a higher specificity than this file's single-class rules.
const PackagePriceComponent = ({
  intl,
  price,
  pricingModel,
  showUnit,
  className,
  amountClassName,
}) => (
  <span className={classNames(css.root, className)}>
    <span className={classNames(css.amount, amountClassName)}>
      {formatPackagePrice(intl, price)}
    </span>
    {showUnit ? (
      <>
        {' '}
        <span className={css.unit}>
          <FormattedMessage id={priceUnitMessageId(pricingModel)} />
        </span>
      </>
    ) : null}
  </span>
);

PackagePriceComponent.defaultProps = { showUnit: true, className: null, amountClassName: null };

PackagePriceComponent.propTypes = {
  intl: intlShape.isRequired,
  price: shape({ amount: number.isRequired, currency: string.isRequired }).isRequired,
  pricingModel: oneOf(PRICING_MODELS).isRequired,
  showUnit: bool,
  className: string,
  amountClassName: string,
};

export const PackagePrice = injectIntl(PackagePriceComponent);
